import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";
import { ProductCard } from "@/components/ProductCard";
import { COMING_SOON } from "@/context/CartContext";
import { AnimalDetailModal } from "@/components/AnimalDetailModal";
import { Footer } from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
    { key: "all", label: "All", testid: "shop-category-all" },
    { key: "snakes", label: "Snakes", testid: "shop-category-snakes" },
    { key: "lizards", label: "Lizards", testid: "shop-category-lizards" },
    { key: "amphibians", label: "Amphibians", testid: "shop-category-amphibians" },
    { key: "invertebrates", label: "Invertebrates", testid: "shop-category-invertebrates" },
    { key: "mammals", label: "Mammals", testid: "shop-category-mammals" },
    { key: "plants", label: "Plants", testid: "shop-category-plants" },
    { key: "fish", label: "Fish", testid: "shop-category-fish" },
    { key: "merch", label: "Merchandise", testid: "shop-category-merch" },
    { key: "caresheets", label: "Care Sheets", testid: "shop-category-caresheets" },
];

export default function ShopPage() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [category, setCategory] = useState("all");
    const [sort, setSort] = useState("featured");
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        setLoading(true);
        axios
            .get(`${API}/products`, { params: category === "all" ? {} : { category } })
            .then((r) => setProducts(r.data))
            .catch(() => setProducts([]))
            .finally(() => setLoading(false));
    }, [category]);

    const sorted = useMemo(() => {
        const list = [...products];
        if (sort === "price-asc") list.sort((a, b) => a.price - b.price);
        else if (sort === "price-desc") list.sort((a, b) => b.price - a.price);
        else if (sort === "rarity") {
            const rank = { "ultra-rare": 0, rare: 1, common: 2 };
            list.sort((a, b) => (rank[a.rarity] ?? 3) - (rank[b.rarity] ?? 3));
        } else list.sort((a, b) => Number(b.featured) - Number(a.featured));
        return list;
    }, [products, sort]);

    return (
        <main className="pt-28">
            <Seo
                title="Shop Captive-Bred Reptiles & Exotics — Logan's Reptiles & Exotics"
                siteName="Logan's Reptiles & Exotics"
                description="Browse rare captive-bred snakes, lizards, spiders, frogs and exotic mammals. Live arrival guarantee on every animal."
            />
            <div className="mx-auto max-w-7xl px-6 sm:px-12">
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="font-mono2 text-xs uppercase tracking-[0.35em] text-red-500"
                >
                    The Collection
                </motion.p>
                <motion.h1
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                    className="mt-4 font-display text-4xl font-extrabold tracking-tight text-bone sm:text-5xl"
                >
                    Into the <span className="font-editorial italic text-red-500">undergrowth</span>
                </motion.h1>

                <div className="mt-10 flex flex-wrap items-center justify-between gap-6 border-y border-red-950/50 py-5">
                    <div className="flex flex-wrap gap-2" data-testid="shop-filters">
                        {CATEGORIES.map((c) => (
                            <button
                                key={c.key}
                                data-testid={c.testid}
                                onClick={() => setCategory(c.key)}
                                className={`border px-4 py-2 font-mono2 text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                                    category === c.key
                                        ? "border-red-700 bg-crimson text-bone"
                                        : "border-red-950/60 text-ash hover:border-red-800 hover:text-bone"
                                }`}
                            >
                                {c.label}
                            </button>
                        ))}
                    </div>
                    <select
                        data-testid="shop-sort-select"
                        value={sort}
                        onChange={(e) => setSort(e.target.value)}
                        className="border border-red-950/60 bg-coal px-3 py-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-ash outline-none focus:border-red-700"
                    >
                        <option value="featured">Featured</option>
                        <option value="rarity">Rarity</option>
                        <option value="price-asc">Price · Low to High</option>
                        <option value="price-desc">Price · High to Low</option>
                    </select>
                </div>

                {loading ? (
                    <p className="py-24 text-center font-editorial text-xl italic text-ash" data-testid="shop-loading">
                        Stirring the leaf litter…
                    </p>
                ) : COMING_SOON || sorted.length === 0 ? (
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        data-testid="shop-coming-soon"
                        className="relative my-16 overflow-hidden border border-red-950/60 bg-coal/60 px-6 py-24 text-center"
                    >
                        <div className="pointer-events-none absolute -left-24 -top-24 h-64 w-64 rounded-full bg-red-900/15 blur-[100px]" />
                        <div className="pointer-events-none absolute -bottom-24 -right-24 h-64 w-64 rounded-full bg-moss/40 blur-[100px]" />
                        <span className="relative mx-auto flex h-3 w-3">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-600 opacity-60" />
                            <span className="relative inline-flex h-3 w-3 rounded-full bg-red-700" />
                        </span>
                        <p className="text-stroke-crimson mt-8 font-display text-4xl font-extrabold uppercase tracking-[0.15em] sm:text-6xl">
                            Coming Soon
                        </p>
                        <p className="mt-6 font-editorial text-lg italic text-ash sm:text-xl">
                            This habitat is being stocked. Something wild is on its way.
                        </p>
                    </motion.div>
                ) : (
                    <div className="grid grid-cols-1 gap-6 py-12 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="shop-grid">
                        {sorted.map((p, i) => (
                            <ProductCard key={p.id} product={p} index={i} onOpen={setSelected} />
                        ))}
                    </div>
                )}
            </div>
            <AnimalDetailModal product={selected} onClose={() => setSelected(null)} />
            <Footer />
        </main>
    );
}
